import { LitElement, css, html } from 'lit'

import {
  CARD_ICON_URLS,
  getStatusIcon,
  renderCardIcon,
} from '../term-card/term-card-icons.js'
import { formatTermDate } from '../../utils/format-date.js'
import { formatPriceAmount } from '../../utils/format-price.js'

export class CtCatalogTermCard extends LitElement {
  static properties = {
    program: { attribute: false },
    term: { attribute: false },
  }

  constructor() {
    super()
    this.program = null
    this.term = null
  }

  static styles = css`
    :host {
      display: block;
      min-width: 0;
      color: #33404c;
      font-family: Poppins, Arial, sans-serif;
    }

    * { box-sizing: border-box; }

    .card {
      display: grid;
      height: 124px;
      min-height: 124px;
      grid-template-columns: 230px minmax(0, 1fr);
      overflow: hidden;
      border: 1px solid #e1e8e8;
      border-radius: 16px;
      background: #fff;
      box-shadow: 0 2px 6px rgba(0,0,0,.06);
      color: inherit;
      cursor: pointer;
      text-decoration: none;
      transition: border-color 180ms ease, box-shadow 180ms ease;
    }

    .card,
    .card * { cursor: pointer; }

    .card:hover {
      border-color: #cfd8d8;
      box-shadow: 0 3px 9px rgba(0,0,0,.12);
    }

    .card:focus-visible {
      outline: 3px solid rgba(247,147,30,.35);
      outline-offset: 3px;
    }

    .visual {
      position: relative;
      height: 122px;
      min-height: 122px;
      overflow: hidden;
      background: #38636a;
    }

    .image {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    .image-hover-overlay {
      position: absolute;
      z-index: 1;
      inset: 0;
      background: rgba(255,255,255,.1);
      opacity: 0;
      pointer-events: none;
      transition: opacity 180ms ease;
    }

    .card:hover .image-hover-overlay { opacity: 1; }

    .duration {
      position: absolute;
      z-index: 2;
      top: 8px;
      padding: 5px 9px;
      border-radius: 16px;
      font-size: 13px;
      font-weight: 500;
      line-height: 18px;
    }

    .duration {
      display: inline-flex;
      right: 8px;
      align-items: center;
      gap: 7px;
      padding-right: 10px;
      background: rgba(255,255,255,.92);
      color: #33404c;
      backdrop-filter: blur(8px);
    }

    .duration svg {
      width: 17px;
      height: 17px;
      fill: none;
      stroke: currentColor;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.8;
    }

    .content {
      --desktop-purchase-width: 330px;
      display: grid;
      min-width: 0;
      grid-template-columns: minmax(0, 1fr) minmax(var(--desktop-purchase-width), auto);
      grid-template-rows: auto auto;
      align-items: center;
      gap: 8px 0;
      padding: 8px 16px;
    }

    .main-info {
      display: grid;
      min-width: 0;
      grid-column: 1;
      grid-row: 1 / 3;
      grid-template-rows: auto auto;
      align-items: center;
      gap: 4px;
      padding-right: 16px;
    }

    .info-separator { display: none; }

    .identity {
      display: grid;
      min-width: 0;
      grid-template-columns: minmax(0, 1fr);
      align-items: start;
      gap: 14px;
    }

    .program-name {
      display: flex;
      min-width: 0;
      grid-row: 1;
      align-items: baseline;
      gap: 5px;
      white-space: nowrap;
    }

    .destination {
      display: inline;
      flex: 0 0 auto;
      margin-bottom: 0;
      color: #f7931e;
      font-size: 19px;
      font-weight: 600;
      line-height: 22px;
      letter-spacing: 0;
      text-transform: none;
    }

    .destination::after { content: ':'; }

    h3 {
      min-width: 0;
      margin: 0;
      overflow: hidden;
      color: #2a4148;
      font-family: Poppins, Arial, sans-serif;
      font-size: 19px;
      font-weight: 500;
      line-height: 22px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .date {
      display: flex;
      grid-row: 2;
      align-items: center;
      gap: 8px;
      margin: 0;
      color: #43899d;
      font-size: 18px;
      font-weight: 500;
      line-height: 24px;
      transform: translateY(-4px);
      white-space: nowrap;
    }

    .date svg {
      width: 25px;
      height: 25px;
      flex: 0 0 25px;
      fill: none;
      stroke: #43899d;
      stroke-linecap: round;
      stroke-linejoin: round;
      stroke-width: 1.8;
      transform: translateY(-1px);
    }

    .details {
      position: relative;
      display: grid;
      min-width: 0;
      grid-template-columns: repeat(2, minmax(0, 1fr));
      gap: 18px;
      padding-top: 12px;
      transform: translateY(2px);
    }

    .details::before {
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      height: 1px;
      background: #e7ecec;
      content: '';
    }

    .detail {
      display: grid;
      min-width: 0;
      grid-template-columns: 20px minmax(0, 1fr);
      align-items: center;
      gap: 4px;
    }

    .detail-icon {
      width: 20px;
      height: 20px;
      display: grid;
      color: #18586a;
      place-items: center;
    }

    .detail-icon svg,
    .detail-icon img {
      width: 18px;
      height: 18px;
      display: block;
      object-fit: contain;
    }

    .detail-value {
      display: block;
      overflow: hidden;
      color: #52666c;
      font-size: 14px;
      font-weight: 500;
      line-height: 20px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .purchase {
      display: flex;
      min-width: var(--desktop-purchase-width);
      grid-column: 2;
      grid-row: 1 / 3;
      align-self: stretch;
      flex-direction: column;
      justify-content: center;
      margin: -8px -16px -8px 0;
      padding: 8px 16px;
    }

    .purchase--available { background: #edf8ef; }
    .purchase--last-places { background: #fff3e6; }
    .purchase--sold-out { background: #fceded; }
    .purchase--unknown { background: #eef0f1; }

    .status {
      display: flex;
      width: 100%;
      margin: 0;
      align-items: center;
      flex-direction: row;
      flex-wrap: nowrap;
      justify-content: flex-start;
      gap: 8px;
      padding: 5px 0;
      border-radius: 10px;
      font-size: 16px;
      font-weight: 600;
      letter-spacing: .03em;
      line-height: 16px;
      text-align: center;
      text-transform: uppercase;
    }

    .status-text { white-space: nowrap; }

    .status-icon {
      width: 20px;
      height: 20px;
      display: inline-grid;
      flex: 0 0 20px;
      place-items: center;
      transform: translateY(-1px);
    }

    .status-icon svg,
    .status-icon img {
      width: 22px;
      height: 22px;
      display: block;
      object-fit: contain;
    }

    .status--available {
      color: #48a158;
      background: transparent;
    }

    .status--last-places {
      color: #c97323;
      background: transparent;
    }

    .status--sold-out {
      color: #bd3434;
      background: transparent;
    }

    .status--unknown {
      color: #68737a;
      background: transparent;
    }

    .purchase-row {
      display: grid;
      grid-template-columns: 92px minmax(155px, 1fr);
      align-items: center;
      gap: 16px;
      margin-top: 10px;
    }

    .price-block {
      display: flex;
      min-width: 0;
      min-height: 40px;
      flex-direction: column;
      justify-content: center;
      margin-left: 4px;
    }

    .price {
      display: block;
      color: #2f8a4c;
      font-size: 24px;
      font-weight: 600;
      line-height: 27px;
      white-space: nowrap;
    }

    .price-label {
      display: block;
      margin-top: -2px;
      color: #849094;
      font-size: 11px;
      line-height: 13px;
    }

    .cta {
      display: inline-flex;
      min-height: 44px;
      align-items: center;
      justify-content: center;
      padding: 0 14px;
      border-radius: 12px;
      background: #f7931e;
      color: #fff;
      font-size: 16px;
      font-weight: 500;
      letter-spacing: .04em;
      line-height: 1;
      text-transform: uppercase;
      white-space: nowrap;
      transition: background 160ms ease, transform 160ms ease;
    }

    .cta:hover {
      background: #ff9f2e;
      transform: translateY(-1px);
    }

    @media (min-width: 1200px) {
      .purchase { margin-left: -16px; }

      .details::before { right: 16px; }

      .purchase-row {
        grid-template-columns: 108px minmax(155px, 1fr);
      }

      .details .detail:nth-child(2) {
        transform: translateX(-16px);
      }
    }

    @media (min-width: 768px) and (max-width: 1199px) {
      .card {
        height: 124px;
        min-height: 124px;
        grid-template-columns: clamp(135px, calc(-86px + 28.8vw), 230px) minmax(0, 1fr);
      }
      .visual { height: 122px; min-height: 122px; }
      .content {
        --tablet-inline-padding: clamp(10px, 1.34vw, 16px);
        --tablet-purchase-width: clamp(210px, calc(-4px + 27.85vw), 330px);
        position: relative;
        grid-template-columns: minmax(0, 1fr) var(--tablet-purchase-width);
        gap: 4px 0;
        padding: 4px var(--tablet-inline-padding);
      }
      .main-info {
        align-items: stretch;
        justify-content: space-between;
        grid-template-rows: auto auto;
        gap: 4px;
        padding-right: clamp(10px, 1.34vw, 16px);
      }
      .identity {
        grid-template-columns: minmax(0, 1fr);
        gap: clamp(10px, 1.17vw, 16px);
        transform: none;
      }
      .destination {
        font-size: clamp(15px, 2vw, 20px);
        line-height: clamp(19px, 1.84vw, 22px);
      }
      h3 { font-size: clamp(15px, 2vw, 20px); line-height: clamp(19px, 1.84vw, 22px); }
      .date {
        align-self: start;
        align-items: center;
        flex-direction: row;
        gap: 6px;
        font-size: clamp(15px, 1.5vw, 18px);
        line-height: 20px;
      }
      .date svg {
        width: clamp(17px, 1.67vw, 20px);
        height: clamp(17px, 1.67vw, 20px);
        flex-basis: clamp(17px, 1.67vw, 20px);
        transform: translateY(-1px);
      }
      .details {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        gap: clamp(8px, 1.5vw, 18px);
        align-self: end;
        padding-top: 12px;
        transform: translateY(6px);
      }
      .details::before { display: none; }
      .info-separator {
        position: absolute;
        z-index: 1;
        bottom: 40px;
        right: calc(
          var(--tablet-purchase-width) +
          var(--tablet-inline-padding) +
          var(--tablet-inline-padding)
        );
        left: var(--tablet-inline-padding);
        display: block;
        height: 1px;
        margin: 0;
        background: #e7ecec;
      }
      .detail-value { font-size: clamp(10px, 1.35vw, 14px); }
      .purchase {
        min-width: 0;
        justify-content: space-between;
        margin-top: -8px;
        margin-right: clamp(-16px, -1.34vw, -10px);
        margin-bottom: -8px;
        padding: 8px clamp(10px, 1vw, 12px) 8px clamp(12px, 1.17vw, 14px);
      }
      .status {
        gap: clamp(4px, .67vw, 8px);
        padding: 0;
        font-size: clamp(15px, 1.4vw, 16px);
        line-height: 17px;
        transform: translateY(10px);
      }
      .status-icon { width: 17px; height: 17px; flex-basis: 17px; }
      .status-icon svg,
      .status-icon img { width: 20px; height: 20px; }
      .purchase-row {
        grid-template-columns: 1fr;
        gap: 2px;
        margin-top: 5px;
        transform: translateY(-8px);
      }
      .price-block { min-height: 26px; margin-left: 0; }
      .price-block {
        align-items: baseline;
        flex-direction: row;
        justify-content: flex-start;
        gap: 6px;
      }
      .price { font-size: clamp(22px, 2.08vw, 24px); line-height: 26px; }
      .price-label {
        margin-top: 0;
        font-size: clamp(12px, .92vw, 14px);
        line-height: 12px;
        white-space: nowrap;
      }
      .cta {
        min-height: clamp(36px, 3.67vw, 40px);
        padding-inline: clamp(8px, 1.17vw, 14px);
        font-size: clamp(14px, 1.8vw, 18px);
      }
    }

    @media (max-width: 767px) {
      .card {
        --mobile-info-padding: clamp(8px, 2.6vw, 16px);
        height: auto;
        min-height: 0;
        grid-template-columns: clamp(98px, calc(-68px + 49vw), 308px) minmax(0, 1fr);
        grid-template-rows: auto auto;
        row-gap: 8px;
      }
      .visual {
        height: auto;
        min-height: 0;
        grid-column: 1;
        grid-row: 1;
        align-self: stretch;
        margin:
          8px
          0
          0
          8px;
        border-radius: 12px;
      }
      .image {
        position: absolute;
        inset: 0;
      }
      .duration {
        top: 6px;
        right: 6px;
        gap: 4px;
        padding: 3px 7px;
        font-size: clamp(12px, 2.4vw, 14px);
        line-height: 16px;
      }
      .duration svg { width: 14px; height: 14px; }
      .content { display: contents; }
      .main-info {
        grid-column: 2;
        grid-row: 1;
        grid-template-rows: auto auto;
        align-content: start;
        gap: clamp(4px, 1.5vw, 6px);
        padding:
          10px
          var(--mobile-info-padding)
          6px;
      }
      .identity {
        grid-template-columns: minmax(0, 1fr);
        gap: clamp(6px, 1.3vw, 8px);
      }
      .program-name {
        display: block;
        grid-row: 1;
        transform: none;
        white-space: normal;
      }
      .destination {
        display: block;
        margin-bottom: 1px;
        font-size: clamp(14px, 3.8vw, 18px);
        font-weight: 600;
        line-height: clamp(16px, 4vw, 20px);
      }
      .destination::after { content: none; }
      h3 {
        overflow: visible;
        font-size: clamp(14px, 4.1vw, 20px);
        line-height: clamp(19px, 4.5vw, 24px);
        text-overflow: clip;
        white-space: normal;
      }
      .date {
        grid-row: 2;
        gap: 8px;
        font-size: clamp(13px, 3.6vw, 16px);
        line-height: clamp(20px, 4vw, 22px);
        transform: translateY(2px);
      }
      .date svg { width: 18px; height: 18px; flex-basis: 18px; }
      .details {
        grid-template-columns: minmax(0, 1fr);
        align-self: start;
        gap: 6px;
        padding-top: 6px;
        transform: none;
      }
      .detail { grid-template-columns: 18px minmax(0, 1fr); gap: 8px; }
      .detail-icon { width: 18px; height: 18px; }
      .detail-icon svg,
      .detail-icon img { width: 18px; height: 18px; }
      .detail-value {
        font-size: clamp(11px, 3.4vw, 16px);
        line-height: clamp(18px, 3.7vw, 20px);
      }
      .purchase {
        grid-column: 1 / -1;
        grid-row: 2;
        min-width: 0;
        justify-content: flex-start;
        margin: 0 0 0;
        padding: 0 8px 8px;
      }
      .purchase.purchase--available,
      .purchase.purchase--last-places,
      .purchase.purchase--sold-out,
      .purchase.purchase--unknown { background: #fff; }
      .status {
        width: 100%;
        align-self: stretch;
        gap: 6px;
        padding: 3px;
        border: 1px solid currentColor;
        border-radius: 12px;
        font-size: clamp(13px, 3.4vw, 16px);
        line-height: 16px;
        transform: none;
      }
      .status--available { background: #edf8ef; border-color: #c6e7cc; }
      .status--last-places { background: #fff3e6; border-color: #f3d2ae; }
      .status--sold-out { background: #fceded; border-color: #efcaca; }
      .status--unknown { background: #eef0f1; border-color: #d9dfe1; }
      .status-icon { width: 18px; height: 18px; flex-basis: 18px; }
      .status-icon svg,
      .status-icon img { width: 19px; height: 19px; }
      .purchase-row {
        grid-template-columns: max-content minmax(0, 1fr);
        justify-content: stretch;
        gap: max(32px, 8vw);
        margin-top: 8px;
      }
      .price-block {
        min-height: 40px;
        margin-left: 0;
        transform: translate(4px, -2px);
      }
      .price {
        font-size: clamp(20px, 6vw, 22px);
        line-height: clamp(23px, calc(11px + 3.75vw), 26px);
      }
      .price-label { margin-top: -4px; font-size: 14px; line-height: 14px; }
      .cta {
        min-width: 0;
        min-height: clamp(30px, calc(36.8px + .94vw), 38px);
        padding-inline: clamp(9px, 2.5vw, 14px);
        font-size: clamp(14px, calc(5px + 2.7vw), 16px);
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .card, .image-hover-overlay, .cta { transition: none; }
      .cta:hover { transform: none; }
    }
  `

  renderDetailIcon(type) {
    if (type === 'group') {
      return renderCardIcon(
        CARD_ICON_URLS.group,
        html`
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
            <circle cx="9" cy="8" r="3"></circle>
            <circle cx="17" cy="9" r="2.5"></circle>
            <path d="M3 20c.5-4 2.5-6 6-6s5.5 2 6 6M14 15c3.5 0 5.5 1.7 6 5"></path>
          </svg>
        `
      )
    }

    return renderCardIcon(
      CARD_ICON_URLS.airport,
      html`
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8">
          <path d="m3 15 18-7-2 4-6 2-2 6-2 1v-6l-4 1-2-1Z"></path>
        </svg>
      `
    )
  }

  render() {
    if (!this.program || !this.term) return ''

    const { program, term } = this
    const statusVariant = term.availabilityVariant || 'unknown'

    return html`
      <a class="card" href=${program.pageUrl}>
        <div class="visual">
          <img
            class="image"
            src=${program.image}
            alt=""
            @error=${event => {
              if (
                program.fallbackImage &&
                event.currentTarget.src !== program.fallbackImage
              ) {
                event.currentTarget.src = program.fallbackImage
              }
            }}
            style=${`object-position: ${program.imagePosition || 'center center'}`}
          >
          <span class="image-hover-overlay" aria-hidden="true"></span>
          <span class="duration">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9"></circle>
              <path d="M12 7v5l3 2"></path>
            </svg>
            ${term.duration || program.duration}
          </span>
        </div>

        <div class="content">
          <div class="main-info">
            <div class="identity">
              <div class="program-name">
                <span class="destination">${program.destination}</span>
                <h3>${program.title}</h3>
              </div>
              <p class="date">
                <svg viewBox="0 0 24 24" aria-hidden="true">
                  <rect x="3" y="5" width="18" height="16" rx="3"></rect>
                  <path d="M8 3v4M16 3v4M3 10h18"></path>
                </svg>
                ${formatTermDate(term.startDate, term.endDate)}
              </p>
            </div>

            <span class="info-separator" aria-hidden="true"></span>

            <div class="details">
              <div class="detail">
                <span class="detail-icon" aria-hidden="true">
                  ${this.renderDetailIcon('group')}
                </span>
                <strong class="detail-value">${term.group}</strong>
              </div>
              <div class="detail">
                <span class="detail-icon" aria-hidden="true">
                  ${this.renderDetailIcon('airport')}
                </span>
                <strong class="detail-value">${term.airport}</strong>
              </div>
            </div>
          </div>

          <div class="purchase purchase--${statusVariant}">
            <div class="status status--${statusVariant}">
              <span class="status-icon" aria-hidden="true">
                ${statusVariant !== 'unknown' ? getStatusIcon(statusVariant) : ''}
              </span>
              <span class="status-text">${term.availability}</span>
            </div>
            <div class="purchase-row">
              <div class="price-block">
                <span class="price">${formatPriceAmount(term.price)} zł</span>
                <span class="price-label">za osobę</span>
              </div>
              <span class="cta">Zobacz termin</span>
            </div>
          </div>
        </div>
      </a>
    `
  }
}

if (!customElements.get('ct-catalog-term-card')) {
  customElements.define('ct-catalog-term-card', CtCatalogTermCard)
}
